import React, { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Bell, X, Check, CheckCheck } from 'lucide-react'
import { toast } from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../../shared/api/supabase'
import { useAuth } from '../../shared/contexts/AuthContext'
import { Notification } from '../../lib/types/database.types'
import { NotificationService } from '../../lib/services/notificationService'
import { decryptSignalMessage, initializeSignalProtocol } from '../../shared/services/signalProtocol/signalEncryptionService'
import { getCachedDecryptedMessage } from '../../shared/services/messageCache'
import { debug } from '../../lib/utils/debug'

export default function NotificationBell() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)
  const [showNotifications, setShowNotifications] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [loading, setLoading] = useState(false)
  const [decryptedMessages, setDecryptedMessages] = useState<{ [notificationId: string]: string }>({})







  const fetchUnreadCount = useCallback(async () => {
    if (!user) return
    const count = await NotificationService.getUnreadCount(user.id)
    setUnreadCount(count)
  }, [user])



  // Helper function to decrypt notification message
  const decryptNotificationMessage = useCallback(async (notification: Notification): Promise<string> => {
    if (!user) return notification.message
    
    // Only decrypt message_received notifications
    if (notification.type !== 'message_received' && notification.type !== 'mention_received') {
      return notification.message
    }
    
    // Check if message is encrypted
    if (!notification.message || !notification.message.startsWith('[SIGNAL]')) {
      return notification.message
    }
    
    // Check cache first
    if (notification.related_id) {
      const cached = await getCachedDecryptedMessage(notification.related_id)
      if (cached) {
        return cached.length > 50 ? `${cached.substring(0, 50)}...` : cached
      }
    }
    
    // If we have a related_id (message ID), try to fetch and decrypt the actual message
    if (notification.related_id) {
      try {
        const { data: message, error } = await supabase
          .from('messages')
          .select('*')
          .eq('id', notification.related_id)
          .single()
        
        if (!error && message) {
          // Decrypt the message
          const decrypted = await decryptSignalMessage(message, user.id)
          // Cache it
          if (notification.related_id) {
            // Cache is handled by decryptSignalMessage internally
          }
          return decrypted.length > 50 ? `${decrypted.substring(0, 50)}...` : decrypted
        }
      } catch (error) {
        debug.error('Error decrypting notification message:', error)
      }
    }
    
    // Fallback: return original message if decryption fails
    return notification.message
  }, [user])

  const fetchNotifications = useCallback(async () => {
    if (!user) return

    setLoading(true)
    try {
      // Ensure Signal Protocol is initialized
      await initializeSignalProtocol(user.id)
      
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50)

      if (error) throw error
      
      const fetchedNotifications = data || []
      setNotifications(fetchedNotifications)
      
      // Decrypt messages for message notifications
      const decrypted: { [notificationId: string]: string } = {}
      for (const notification of fetchedNotifications) {
        if (notification.type === 'message_received' || notification.type === 'mention_received') {
          const decryptedMsg = await decryptNotificationMessage(notification)
          decrypted[notification.id] = decryptedMsg
        }
      }
      setDecryptedMessages(decrypted)
    } catch (error: any) {
      console.error('Error fetching notifications:', error)
    } finally {
      setLoading(false)
    }
  }, [user, decryptNotificationMessage])

  const handleNotificationClick = async (notification: Notification) => {
    // Mark as read first
    try {
      await NotificationService.markAsRead(notification.id)
      setNotifications(prev => 
        prev.map(n => n.id === notification.id ? { ...n, is_read: true } : n)
      )
      fetchUnreadCount()
    } catch (error: any) {
      toast.error('Error marking notification as read')
    }

    // Navigate based on notification type
    switch (notification.type) {
      case 'task_assigned':
      case 'task_due':
      case 'task_overdue':
      case 'task_completed':
        // Navigate to tasks page with specific task ID if available
        if (notification.related_id) {
          navigate(`/tasks?taskId=${notification.related_id}`)
        } else {
          navigate('/tasks')
        }
        break
      case 'event_created':
      case 'event_updated':
      case 'event_reminder':
      case 'rsvp_received':
        navigate('/events')
        break
      case 'message_received':
      case 'mention_received':
        navigate('/messages')
        break
      case 'group_invitation':
      case 'group_member_joined':
      case 'group_member_left':
        navigate('/groups')
        break
      case 'curriculum_shared':
        navigate('/curriculum')
        break
      default:
        // For general notifications, just close the dropdown
        break
    }

    // Close the notification dropdown
    setShowNotifications(false)
  }



  const handleMarkAllAsRead = async () => {
    if (!user) return

    try {
      await NotificationService.markAllAsRead(user.id)
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
      setUnreadCount(0)
      toast.success('All notifications marked as read')
    } catch (error: any) {
      toast.error('Error marking all notifications as read')
    }
  }

  const handleDeleteNotification = async (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation()
    
    try {
      await NotificationService.deleteNotification(notificationId)
      setNotifications(prev => prev.filter(n => n.id !== notificationId))
      fetchUnreadCount()
      toast.success('Notification deleted')
    } catch (error: any) {
      toast.error('Error deleting notification')
    }
  }

  // Test function to create a notification
  

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'task_assigned':
      case 'task_due':
      case 'task_overdue':
      case 'task_completed':
        return '📋'
      case 'message_received':
      case 'mention_received':
        return '💬'
      case 'event_created':
      case 'event_updated':
      case 'event_reminder':
      case 'rsvp_received':
        return '📅'
      case 'group_invitation':
      case 'group_member_joined':
        return '👥'
      case 'curriculum_shared':
        return '📚'
      default:
        return '🔔'
    }
  }

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    return `${Math.floor(diffInMinutes / 1440)}d ago`
  }

  const groupNotificationsByDate = (notifications: Notification[]) => {
    const today = new Date()
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000)
    
    const groups: { [key: string]: Notification[] } = {
      today: [],
      yesterday: [],
      older: []
    }

    notifications.forEach(notification => {
      const notificationDate = new Date(notification.created_at)
      if (notificationDate.toDateString() === today.toDateString()) {
        groups.today.push(notification)
      } else if (notificationDate.toDateString() === yesterday.toDateString()) {
        groups.yesterday.push(notification)
      } else {
        groups.older.push(notification)
      }
    })

    return groups
  }



  // Listen for subscription status changes
  useEffect(() => {
    const handleSubscriptionChange = () => {
      fetchUnreadCount()
      fetchNotifications()
    }

    // Listen for custom events when subscription status changes
    window.addEventListener('push-subscription-changed', handleSubscriptionChange)
    
    // Also listen for storage changes (when user enables/disables notifications)
    const handleStorageChange = () => {
      fetchUnreadCount()
      fetchNotifications()
    }
    
    window.addEventListener('storage', handleStorageChange)
    
    return () => {
      window.removeEventListener('push-subscription-changed', handleSubscriptionChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [user, fetchUnreadCount, fetchNotifications])

  // Force refresh when component mounts or user changes
  useEffect(() => {
    if (user) {
      // Small delay to ensure everything is loaded
      const timer = setTimeout(() => {
        fetchUnreadCount()
        fetchNotifications()
      }, 100)
      
      // Also set up a periodic refresh every 30 seconds as fallback
      const intervalTimer = setInterval(() => {
        fetchUnreadCount()
        fetchNotifications()
      }, 30000)
      
      return () => {
        clearTimeout(timer)
        clearInterval(intervalTimer)
      }
    }
  }, [user, fetchUnreadCount, fetchNotifications])

  if (!user) return null

  const groupedNotifications = groupNotificationsByDate(notifications)

  return (
    <div className="relative">
      <button
        onClick={() => setShowNotifications(!showNotifications)}
        className="relative p-2 text-white hover:text-salt-gold hover:bg-gray-800 rounded-lg transition-colors"
        title="Notifications"
      >
        <Bell className="w-6 h-6 text-white" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showNotifications && createPortal(
        <div className="fixed bg-white rounded-lg shadow-xl border border-gray-200 z-50 flex flex-col" 
             style={{ 
               top: '80px',
               right: '1rem',
               width: 'min(384px, calc(100vw - 2rem))',
               maxWidth: 'calc(100vw - 2rem)',
               minWidth: '320px',
               maxHeight: '65vh',
               display: 'flex',
               flexDirection: 'column'
             }}>
          {/* Header */}
          <div className="p-3 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-primary-600 hover:text-primary-700 flex items-center space-x-1 px-2 py-1 border border-primary-300 rounded"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-3 h-3" />
                  <span>Mark All Read</span>
                </button>
              )}
            </div>
          </div>

          {/* Notifications */}
          <div className="flex-1 overflow-y-auto" style={{ minHeight: 0 }}>
            {loading ? (
              <div className="p-4 text-center text-gray-500">
                Loading notifications...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p>No notifications yet</p>
              </div>
            ) : (
              <div>
                {Object.entries(groupedNotifications).map(([groupKey, groupNotifications]) => {
                  if (groupNotifications.length === 0) return null
                  
                  return (
                    <div key={groupKey}>
                      <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
                        <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                          {groupKey === 'today' ? 'Today' : groupKey === 'yesterday' ? 'Yesterday' : 'Older'}
                        </h4>
                      </div>
                      
                      {groupNotifications.map(notification => (
                        <div
                          key={notification.id}
                          className={`p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 group relative ${
                            !notification.is_read ? 'bg-gray-50 border-l-4 border-l-salt-gold-dark' : ''
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <span className="text-xl mt-1">{getNotificationIcon(notification.type)}</span>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-medium text-gray-900 truncate">
                                  {notification.title}
                                </h4>
                                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                                  {decryptedMessages[notification.id] || notification.message}
                                </p>
                                <div className="flex items-center justify-between mt-2">
                                  <p className="text-xs text-gray-400">
                                    {formatTimeAgo(notification.created_at)}
                                  </p>
                                  {notification.is_read && (
                                    <Check className="w-4 h-4 text-green-500" />
                                  )}
                                </div>
                              </div>
                            </div>
                            <button
                              onClick={(e) => handleDeleteNotification(notification.id, e)}
                              className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-500 transition-all ml-2"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={() => navigate('/home')}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              View all notifications
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Click outside to close */}
      {showNotifications && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowNotifications(false)}
        />
      )}
    </div>
  )
} 